import * as React from 'react';
import { useTheme } from '@mui/material/styles';

type Props = {
  size?: number; // width in px; height scales automatically
  title?: string;
  testId?: string;
};

export const MiraLoaderSvg: React.FC<Props> = ({
  size = 240,
  title = 'Loading…',
  testId,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  return (
    <svg
      id="logoLoader"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 127 74.94"
      width={size}
      height={(size * 74.94) / 127}
      role="img"
      aria-label={title}
      data-testid={testId}
      style={{ color: isDarkMode ? '#fff' : '#000' }}
    >
      <title>{title}</title>

      {/* Inline CSS works in React if wrapped in a template string */}
      <style>{`
        /* Force color based on theme */
        #logoLoader { color: ${isDarkMode ? '#fff' : '#000'}; }
        .cls-1 { fill: currentColor; }
        .base  { opacity: .18; }

        .modeLight { display: ${isDarkMode ? 'none' : 'block'}; }
        .modeDark  { display: ${isDarkMode ? 'block' : 'none'}; }
        .filledStatic { display: none; }

        @media (prefers-reduced-motion: reduce) {
          .modeLight, .modeDark { display: none; }
          .filledStatic { display: block; }
        }
      `}</style>

      <defs>
        <linearGradient id="band" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="currentColor" stopOpacity="0" />
          <stop offset="0.45" stopColor="currentColor" stopOpacity="0" />
          <stop offset="0.50" stopColor="currentColor" stopOpacity=".95" />
          <stop offset="0.55" stopColor="currentColor" stopOpacity="0" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="glow" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="currentColor" stopOpacity="0" />
          <stop offset="0.40" stopColor="currentColor" stopOpacity="0" />
          <stop offset="0.55" stopColor="currentColor" stopOpacity=".35" />
          <stop offset="0.70" stopColor="currentColor" stopOpacity="0" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
        <filter id="softGlow" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="2" />
        </filter>

        {/* Masks — left→right reveal, run once & freeze */}
        {/* Light (faster) */}
        <mask id="m1L">
          <rect x="0" y="0" width="0" height="74.94" fill="#fff">
            <animate
              attributeName="width"
              from="0"
              to="127"
              dur="2.2s"
              begin="0s"
              repeatCount="1"
              fill="freeze"
              calcMode="spline"
              keyTimes="0;1"
              keySplines="0.42 0 0.58 1"
            />
          </rect>
        </mask>
        <mask id="m2L">
          <rect x="0" y="0" width="0" height="74.94" fill="#fff">
            <animate
              attributeName="width"
              from="0"
              to="127"
              dur="2.2s"
              begin=".18s"
              repeatCount="1"
              fill="freeze"
              calcMode="spline"
              keyTimes="0;1"
              keySplines="0.42 0 0.58 1"
            />
          </rect>
        </mask>
        <mask id="m3L">
          <rect x="0" y="0" width="0" height="74.94" fill="#fff">
            <animate
              attributeName="width"
              from="0"
              to="127"
              dur="2.2s"
              begin=".36s"
              repeatCount="1"
              fill="freeze"
              calcMode="spline"
              keyTimes="0;1"
              keySplines="0.42 0 0.58 1"
            />
          </rect>
        </mask>
        <mask id="m4L">
          <rect x="0" y="0" width="0" height="74.94" fill="#fff">
            <animate
              attributeName="width"
              from="0"
              to="127"
              dur="2.2s"
              begin=".54s"
              repeatCount="1"
              fill="freeze"
              calcMode="spline"
              keyTimes="0;1"
              keySplines="0.42 0 0.58 1"
            />
          </rect>
        </mask>
        <mask id="m5L">
          <rect x="0" y="0" width="0" height="74.94" fill="#fff">
            <animate
              attributeName="width"
              from="0"
              to="127"
              dur="2.2s"
              begin=".72s"
              repeatCount="1"
              fill="freeze"
              calcMode="spline"
              keyTimes="0;1"
              keySplines="0.42 0 0.58 1"
            />
          </rect>
        </mask>
        <mask id="m6L">
          <rect x="0" y="0" width="0" height="74.94" fill="#fff">
            <animate
              attributeName="width"
              from="0"
              to="127"
              dur="2.2s"
              begin=".90s"
              repeatCount="1"
              fill="freeze"
              calcMode="spline"
              keyTimes="0;1"
              keySplines="0.42 0 0.58 1"
            />
          </rect>
        </mask>
        <mask id="m7L">
          <rect x="0" y="0" width="0" height="74.94" fill="#fff">
            <animate
              attributeName="width"
              from="0"
              to="127"
              dur="2.2s"
              begin="1.08s"
              repeatCount="1"
              fill="freeze"
              calcMode="spline"
              keyTimes="0;1"
              keySplines="0.42 0 0.58 1"
            />
          </rect>
        </mask>
        <mask id="m8L">
          <rect x="0" y="0" width="0" height="74.94" fill="#fff">
            <animate
              attributeName="width"
              from="0"
              to="127"
              dur="2.2s"
              begin="1.26s"
              repeatCount="1"
              fill="freeze"
              calcMode="spline"
              keyTimes="0;1"
              keySplines="0.42 0 0.58 1"
            />
          </rect>
        </mask>

        {/* Dark (slower, smoother) */}
        <mask id="m1D">
          <rect x="0" y="0" width="0" height="74.94" fill="#fff">
            <animate
              attributeName="width"
              from="0"
              to="127"
              dur="3.0s"
              begin="0s"
              repeatCount="1"
              fill="freeze"
              calcMode="spline"
              keyTimes="0;1"
              keySplines="0.33 0 0.67 1"
            />
          </rect>
        </mask>
        <mask id="m2D">
          <rect x="0" y="0" width="0" height="74.94" fill="#fff">
            <animate
              attributeName="width"
              from="0"
              to="127"
              dur="3.0s"
              begin=".25s"
              repeatCount="1"
              fill="freeze"
              calcMode="spline"
              keyTimes="0;1"
              keySplines="0.33 0 0.67 1"
            />
          </rect>
        </mask>
        <mask id="m3D">
          <rect x="0" y="0" width="0" height="74.94" fill="#fff">
            <animate
              attributeName="width"
              from="0"
              to="127"
              dur="3.0s"
              begin=".50s"
              repeatCount="1"
              fill="freeze"
              calcMode="spline"
              keyTimes="0;1"
              keySplines="0.33 0 0.67 1"
            />
          </rect>
        </mask>
        <mask id="m4D">
          <rect x="0" y="0" width="0" height="74.94" fill="#fff">
            <animate
              attributeName="width"
              from="0"
              to="127"
              dur="3.0s"
              begin=".75s"
              repeatCount="1"
              fill="freeze"
              calcMode="spline"
              keyTimes="0;1"
              keySplines="0.33 0 0.67 1"
            />
          </rect>
        </mask>
        <mask id="m5D">
          <rect x="0" y="0" width="0" height="74.94" fill="#fff">
            <animate
              attributeName="width"
              from="0"
              to="127"
              dur="3.0s"
              begin="1.00s"
              repeatCount="1"
              fill="freeze"
              calcMode="spline"
              keyTimes="0;1"
              keySplines="0.33 0 0.67 1"
            />
          </rect>
        </mask>
        <mask id="m6D">
          <rect x="0" y="0" width="0" height="74.94" fill="#fff">
            <animate
              attributeName="width"
              from="0"
              to="127"
              dur="3.0s"
              begin="1.25s"
              repeatCount="1"
              fill="freeze"
              calcMode="spline"
              keyTimes="0;1"
              keySplines="0.33 0 0.67 1"
            />
          </rect>
        </mask>
        <mask id="m7D">
          <rect x="0" y="0" width="0" height="74.94" fill="#fff">
            <animate
              attributeName="width"
              from="0"
              to="127"
              dur="3.0s"
              begin="1.50s"
              repeatCount="1"
              fill="freeze"
              calcMode="spline"
              keyTimes="0;1"
              keySplines="0.33 0 0.67 1"
            />
          </rect>
        </mask>
        <mask id="m8D">
          <rect x="0" y="0" width="0" height="74.94" fill="#fff">
            <animate
              attributeName="width"
              from="0"
              to="127"
              dur="3.0s"
              begin="1.75s"
              repeatCount="1"
              fill="freeze"
              calcMode="spline"
              keyTimes="0;1"
              keySplines="0.33 0 0.67 1"
            />
          </rect>
        </mask>

        {/* Logo segments as clip paths */}
        <clipPath id="c1">
          <path d="M22.62,72.21c-5.83-11.63-10.21-23.14-15.49-35.01h-.19v36.66H0V24.22h6.12c1.39,0,2.64.82,3.21,2.08,5.6,12.47,11.15,24.83,16.76,37.31h.13c5.61-12.48,11.16-24.84,16.76-37.31.57-1.26,1.83-2.08,3.21-2.08h6.19v49.64h-6.94v-36.66h-.25c-5.29,11.9-10.27,23.12-15.57,35.02-.22.48-.84,2.71-3.43,2.71s-3.24-2.07-3.57-2.72Z" />
        </clipPath>
        <clipPath id="c2">
          <path d="M64.7,73.85h-6.49v-35.32h3.84c1.46,0,2.65,1.19,2.65,2.65v32.67Z" />
        </clipPath>
        <clipPath id="c3">
          <path d="M91.8,42.69c0,1.46-1.16,2.38-2.58,2.05l-1.74-.16c-7.83,0-10.25,7.57-10.25,13.75v15.53h-6.49v-35.32h6.36v6.43h.25c1.78-4.26,5.73-7.45,10.69-7.45,1.21,0,2.42.19,3.75.7v4.47Z" />
        </clipPath>
        <clipPath id="c4">
          <path d="M118.93,74.03c4.71,2.42,11.13-4.28,6.43-4s-3.89-5.07-3.97-7.18v-13.17c0-8.33-6.17-12.15-13.68-12.15-6.3,0-12.54,2.74-14.7,9.86.19.82,4.3,2.55,6.17,1.43,1.62-.97,1.16-2.4,2.33-3.82,1.74-2.1,3.74-2.13,5.88-2.13,4.52,0,7.45,2.93,7.45,6.24,0,3.69-3.69,3.88-10.88,5.41-6.43,1.34-12.15,4.07-12.15,10.69s5.53,9.74,11.52,9.74c5.6,0,9.8-2.99,11.84-7.13h.13c0,1.62.79,4.75,3.65,6.22ZM113.88,62.58s-2.8,7.26-8.91,7.26c-3.69,0-6.11-1.78-6.11-4.9,0-4.07,4.9-5.47,8.27-6.17,3.5-.7,5.27-1.48,5.27-1.48,1.33-.59,2.43-.19,2.43.87s-.43, 3.06-.95,4.42Z" />
        </clipPath>
        <clipPath id="c5">
          <path d="M57,26.7c0-2.35,1.91-4.39,4.52-4.39s4.39,2.04,4.39,4.39c0,2.61-1.91,4.58-4.39,4.58-2.61,0-4.52-1.97-4.52-4.58Z" />
        </clipPath>
        <clipPath id="c6">
          <path d="M69.94,26.7c0-2.35,1.91-4.39,4.52-4.39s4.39,2.04,4.39,4.39c0,2.61-1.91,4.58-4.39,4.58-2.61,0-4.52-1.97-4.52-4.58Z" />
        </clipPath>
        <clipPath id="c7">
          <path d="M82.89,26.7c0-2.35,1.91-4.39,4.52-4.39s4.39,2.04,4.39,4.39c0,2.61-1.91,4.58-4.39,4.58-2.61,0-4.52-1.97-4.52-4.58Z" />
        </clipPath>
        <clipPath id="c8">
          <path d="M74.4,0c-22.79,0-40.64,11.77-40.64,26.79,0,3.06.82,5.96,2.2,8.67l4.57-10.18c1.35-10.25,16.3-18.65,33.88-18.65s34.01,9.23,34.01,20.16c0,2.93-1.15,5.73-3.15,8.28.81-.09,1.6-.21,2.45-.21,1.87,0,3.57.27,5.16.66,1.39-2.73,2.17-5.66,2.17-8.73,0-15.02-17.85-26.79-40.64-26.79Z" />
        </clipPath>

        {/* Reusable segment content */}
        <g id="segL">
          <rect width="127" height="74.94" className="cls-1" />
          <rect
            x="-160"
            y="-10"
            width="220"
            height="100"
            fill="url(#glow)"
            filter="url(#softGlow)"
            opacity=".5"
          >
            <animate
              attributeName="x"
              from="-160"
              to="187"
              dur="2.0s"
              repeatCount="1"
              fill="freeze"
              calcMode="spline"
              keyTimes="0;1"
              keySplines="0.33 0 0.67 1"
            />
          </rect>
          <rect x="-140" y="-10" width="180" height="100" fill="url(#band)">
            <animate
              attributeName="x"
              from="-140"
              to="167"
              dur="1.4s"
              repeatCount="1"
              fill="freeze"
              calcMode="spline"
              keyTimes="0;1"
              keySplines="0.33 0 0.67 1"
            />
          </rect>
        </g>
        <g id="segD">
          <rect width="127" height="74.94" className="cls-1" />
          <rect
            x="-160"
            y="-10"
            width="220"
            height="100"
            fill="url(#glow)"
            filter="url(#softGlow)"
            opacity=".5"
          >
            <animate
              attributeName="x"
              from="-160"
              to="187"
              dur="2.6s"
              repeatCount="1"
              fill="freeze"
              calcMode="spline"
              keyTimes="0;1"
              keySplines="0.33 0 0.67 1"
            />
          </rect>
          <rect x="-140" y="-10" width="180" height="100" fill="url(#band)">
            <animate
              attributeName="x"
              from="-140"
              to="167"
              dur="1.8s"
              repeatCount="1"
              fill="freeze"
              calcMode="spline"
              keyTimes="0;1"
              keySplines="0.33 0 0.67 1"
            />
          </rect>
        </g>
      </defs>

      {/* Dim base logo */}
      <g className="base cls-1">
        <g>
          <g>
            <path d="M22.62,72.21c-5.83-11.63-10.21-23.14-15.49-35.01h-.19v36.66H0V24.22h6.12c1.39,0,2.64.82,3.21,2.08,5.6,12.47,11.15,24.83,16.76,37.31h.13c5.61-12.48,11.16-24.84,16.76-37.31.57-1.26,1.83-2.08,3.21-2.08h6.19v49.64h-6.94v-36.66h-.25c-5.29,11.9-10.27,23.12-15.57,35.02-.22.48-.84,2.71-3.43,2.71s-3.24-2.07-3.57-2.72Z" />
            <path d="M64.7,73.85h-6.49v-35.32h3.84c1.46,0,2.65,1.19,2.65,2.65v32.67Z" />
            <path d="M91.8,42.69c0,1.46-1.16,2.38-2.58,2.05l-1.74-.16c-7.83,0-10.25,7.57-10.25,13.75v15.53h-6.49v-35.32h6.36v6.43h.25c1.78-4.26,5.73-7.45,10.69-7.45,1.21,0,2.42.19,3.75.7v4.47Z" />
            <path d="M118.93,74.03c4.71,2.42,11.13-4.28,6.43-4s-3.89-5.07-3.97-7.18v-13.17c0-8.33-6.17-12.15-13.68-12.15-6.3,0-12.54,2.74-14.7,9.86.19.82,4.3,2.55,6.17,1.43,1.62-.97,1.16-2.4,2.33-3.82,1.74-2.1,3.74-2.13,5.88-2.13,4.52,0,7.45,2.93,7.45,6.24,0,3.69-3.69,3.88-10.88,5.41-6.43,1.34-12.15,4.07-12.15,10.69s5.53,9.74,11.52,9.74c5.6,0,9.8-2.99,11.84-7.13h.13c0,1.62.79,4.75,3.65,6.22ZM113.88,62.58s-2.8,7.26-8.91,7.26c-3.69,0-6.11-1.78-6.11-4.9,0-4.07,4.9-5.47,8.27-6.17,3.5-.7,5.27-1.48,5.27-1.48,1.33-.59,2.43-.19,2.43.87s-.43, 3.06-.95,4.42Z" />
          </g>
          <g>
            <path d="M57,26.7c0-2.35,1.91-4.39,4.52-4.39s4.39,2.04,4.39,4.39c0,2.61-1.91,4.58-4.39,4.58-2.61,0-4.52-1.97-4.52-4.58Z" />
            <path d="M69.94,26.7c0-2.35,1.91-4.39,4.52-4.39s4.39,2.04,4.39,4.39c0,2.61-1.91,4.58-4.39,4.58-2.61,0-4.52-1.97-4.52-4.58Z" />
            <path d="M82.89,26.7c0-2.35,1.91-4.39,4.52-4.39s4.39,2.04,4.39,4.39c0,2.61-1.91,4.58-4.39,4.58-2.61,0-4.52-1.97-4.52-4.58Z" />
          </g>
          <path d="M74.4,0c-22.79,0-40.64,11.77-40.64,26.79,0,3.06.82,5.96,2.2,8.67l4.57-10.18c1.35-10.25,16.3-18.65,33.88-18.65s34.01,9.23,34.01,20.16c0,2.93-1.15,5.73-3.15,8.28.81-.09,1.6-.21,2.45-.21,1.87,0,3.57.27,5.16.66,1.39-2.73,2.17-5.66,2.17-8.73,0-15.02-17.85-26.79-40.64-26.79Z" />
        </g>
      </g>

      {/* Light timings */}
      <g className="modeLight">
        <g clipPath="url(#c1)" mask="url(#m1L)">
          <use href="#segL" />
        </g>
        <g clipPath="url(#c2)" mask="url(#m2L)">
          <use href="#segL" />
        </g>
        <g clipPath="url(#c3)" mask="url(#m3L)">
          <use href="#segL" />
        </g>
        <g clipPath="url(#c4)" mask="url(#m4L)">
          <use href="#segL" />
        </g>
        <g clipPath="url(#c5)" mask="url(#m5L)">
          <use href="#segL" />
        </g>
        <g clipPath="url(#c6)" mask="url(#m6L)">
          <use href="#segL" />
        </g>
        <g clipPath="url(#c7)" mask="url(#m7L)">
          <use href="#segL" />
        </g>
        <g clipPath="url(#c8)" mask="url(#m8L)">
          <use href="#segL" />
        </g>
      </g>

      {/* Dark timings */}
      <g className="modeDark">
        <g clipPath="url(#c1)" mask="url(#m1D)">
          <use href="#segD" />
        </g>
        <g clipPath="url(#c2)" mask="url(#m2D)">
          <use href="#segD" />
        </g>
        <g clipPath="url(#c3)" mask="url(#m3D)">
          <use href="#segD" />
        </g>
        <g clipPath="url(#c4)" mask="url(#m4D)">
          <use href="#segD" />
        </g>
        <g clipPath="url(#c5)" mask="url(#m5D)">
          <use href="#segD" />
        </g>
        <g clipPath="url(#c6)" mask="url(#m6D)">
          <use href="#segD" />
        </g>
        <g clipPath="url(#c7)" mask="url(#m7D)">
          <use href="#segD" />
        </g>
        <g clipPath="url(#c8)" mask="url(#m8D)">
          <use href="#segD" />
        </g>
      </g>

      {/* Reduced motion: static filled logo */}
      <g className="filledStatic cls-1">
        <g>
          <g>
            <path d="M22.62,72.21c-5.83-11.63-10.21-23.14-15.49-35.01h-.19v36.66H0V24.22h6.12c1.39,0,2.64.82,3.21,2.08,5.6,12.47,11.15,24.83,16.76,37.31h.13c5.61-12.48,11.16-24.84,16.76-37.31.57-1.26,1.83-2.08,3.21-2.08h6.19v49.64h-6.94v-36.66h-.25c-5.29,11.9-10.27,23.12-15.57,35.02-.22.48-.84,2.71-3.43,2.71s-3.24-2.07-3.57-2.72Z" />
            <path d="M64.7,73.85h-6.49v-35.32h3.84c1.46,0,2.65,1.19,2.65,2.65v32.67Z" />
            <path d="M91.8,42.69c0,1.46-1.16,2.38-2.58,2.05l-1.74-.16c-7.83,0-10.25,7.57-10.25,13.75v15.53h-6.49v-35.32h6.36v6.43h.25c1.78-4.26,5.73-7.45,10.69-7.45,1.21,0,2.42.19,3.75.7v4.47Z" />
            <path d="M118.93,74.03c4.71,2.42,11.13-4.28,6.43-4s-3.89-5.07-3.97-7.18v-13.17c0-8.33-6.17-12.15-13.68-12.15-6.3,0-12.54,2.74-14.7,9.86.19.82,4.3,2.55,6.17,1.43,1.62-.97,1.16-2.4,2.33-3.82,1.74-2.1,3.74-2.13,5.88-2.13,4.52,0,7.45,2.93,7.45,6.24,0,3.69-3.69,3.88-10.88,5.41-6.43,1.34-12.15,4.07-12.15,10.69s5.53,9.74,11.52,9.74c5.6,0,9.8-2.99, 11.84-7.13h.13c0,1.62.79,4.75,3.65,6.22ZM113.88,62.58s-2.8,7.26-8.91,7.26c-3.69,0-6.11-1.78-6.11-4.9,0-4.07,4.9-5.47,8.27-6.17,3.5-.7,5.27-1.48,5.27-1.48,1.33-.59,2.43-.19,2.43.87s-.43, 3.06-.95,4.42Z" />
          </g>
          <g>
            <path d="M57,26.7c0-2.35,1.91-4.39,4.52-4.39s4.39,2.04,4.39,4.39c0,2.61-1.91,4.58-4.39,4.58-2.61,0-4.52-1.97-4.52-4.58Z" />
            <path d="M69.94,26.7c0-2.35,1.91-4.39,4.52-4.39s4.39,2.04,4.39,4.39c0,2.61-1.91,4.58-4.39,4.58-2.61,0-4.52-1.97-4.52-4.58Z" />
            <path d="M82.89,26.7c0-2.35,1.91-4.39,4.52-4.39s4.39,2.04,4.39,4.39c0,2.61-1.91,4.58-4.39,4.58-2.61,0-4.52-1.97-4.52-4.58Z" />
          </g>
          <path d="M74.4,0c-22.79,0-40.64,11.77-40.64,26.79,0,3.06.82,5.96,2.2,8.67l4.57-10.18c1.35-10.25,16.3-18.65,33.88-18.65s34.01,9.23,34.01,20.16c0,2.93-1.15,5.73-3.15,8.28.81-.09,1.6-.21,2.45-.21,1.87,0,3.57.27,5.16.66,1.39-2.73,2.17-5.66,2.17-8.73,0-15.02-17.85-26.79-40.64-26.79Z" />
        </g>
      </g>
    </svg>
  );
};
